import { Input, ModuleWrapper } from '@/components'
import { Button as BaseButton } from '@/components_deprecated'
import { Card } from '@/components_deprecated/Card'
import { PlainLink } from '@/components_deprecated/Link'
import { LeaderGroupSelect } from '@/components_deprecated/Select/LeaderGroupSelect'
import { PermissionsRequired } from '@/containers/permission-containers'
import { PageType } from '@/modules/common/consts'
import { useDebounce, useLeaderGroups } from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { useTranslate } from '@ur-solutions/react-hooks'
import React, { useEffect, useMemo, useState } from 'react'
import { BrowserView, MobileView } from 'react-device-detect'
import styled from 'styled-components'
import type { Tab } from '../../../components_deprecated/Tabs'
import { TabPane, Tabs } from '../../../components_deprecated/Tabs'
import { ThreadsTable } from '../components'
import { ForumTabKey } from '../types'

const Controls = styled(Card)`
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-flow: column;
  gap: 1rem;

  margin-bottom: 1rem;
  padding: 1rem;
`
const Button = styled(BaseButton)`
  ${props => props.theme.media.mobile} {
    padding: 0 3rem 0 1rem;
  }
`

export const Forum: React.FC = () => {
  const translations = useTranslate({
    forum: 'common.forum',
    searchInForum: 'forum.search',
    newThread: 'forum.new-thread',
    chooseGroup: 'common.choose-group',

    general: 'common.general',
    direct: 'common.direct',
  })

  const tabs = useMemo<[Tab, Tab]>(
    () => [
      { key: ForumTabKey.GENERAL, text: translations.general },
      { key: ForumTabKey.DIRECT, text: translations.direct },
    ],
    [translations.general, translations.direct]
  )

  const [query, setQuery] = useState('')
  const debouncedQuery = useDebounce(query)

  const [sort, setSort] = useState<string>(
    localStorage.getItem('forumSort') || '-createdAt'
  )
  const [selectedLeaderGroup, setSelectedLeaderGroup] = useState<string | null>(
    null
  )

  const { hasAccess, myLeaderGroups, groupsLoading } = useLeaderGroups()

  function getInitialTab() {
    const savedTabKey = localStorage.getItem('forumActiveTab')

    if (savedTabKey === ForumTabKey.DIRECT) return tabs[1]
    return tabs[0]
  }

  const [activeTab, setActiveTab] = useState<Tab>(getInitialTab())

  useEffect(() => {
    localStorage.setItem('forumSort', sort || '-createdAt')
  }, [sort])

  useEffect(() => {
    localStorage.setItem('forumActiveTab', activeTab.key)
  }, [activeTab.key])

  const createLink =
    activeTab.key === ForumTabKey.GENERAL
      ? '/forum/create'
      : '/forum/create-direct'

  return (
    <ModuleWrapper
      title={translations.forum}
      page={PageType.FORUM}
      headerRight={
        <MobileView>
          <PermissionsRequired permissions={PERMISSIONS.forum.add.thread}>
            <PlainLink to={createLink}>
              <Button
                iconLeftProps={{ icon: 'plus', margin: '0' }}
                height="42px"
                padding="0"
                fontSize="0.8em"
              >
                {translations.newThread}
              </Button>
            </PlainLink>
          </PermissionsRequired>
        </MobileView>
      }
    >
      <Tabs
        activeTab={activeTab}
        tabs={tabs}
        onChange={tab => setActiveTab(tab)}
      />

      <TabPane active={activeTab.key === ForumTabKey.GENERAL}>
        <Controls>
          <Input
            value={query}
            type="search"
            placeholder={translations.searchInForum}
            onChange={setQuery}
          />

          <BrowserView>
            <PermissionsRequired permissions={PERMISSIONS.forum.add.thread}>
              <PlainLink to="/forum/create">
                <Button iconLeftProps={{ icon: 'plus' }}>
                  {translations.newThread}
                </Button>
              </PlainLink>
            </PermissionsRequired>
          </BrowserView>
        </Controls>

        <ThreadsTable
          query={debouncedQuery}
          sort={sort}
          onSort={setSort}
          leaderGroupId={selectedLeaderGroup}
        />
      </TabPane>

      <TabPane active={activeTab.key === ForumTabKey.DIRECT}>
        <Controls>
          <Input
            value={query}
            type="search"
            placeholder={translations.searchInForum}
            onChange={setQuery}
          />

          {hasAccess && (
            <LeaderGroupSelect
              value={selectedLeaderGroup}
              loading={groupsLoading}
              options={
                myLeaderGroups?.map(group => ({
                  label: group.name,
                  value: group.id,
                })) ?? []
              }
              fullWidth
              grayed={true}
              onChange={value => setSelectedLeaderGroup(value)}
              placeholder={translations.chooseGroup}
              nullable
            />
          )}

          <BrowserView>
            <PermissionsRequired permissions={PERMISSIONS.forum.add.thread}>
              <PlainLink to="/forum/create-direct">
                <Button iconLeftProps={{ icon: 'plus' }}>
                  {translations.newThread}
                </Button>
              </PlainLink>
            </PermissionsRequired>
          </BrowserView>
        </Controls>

        <ThreadsTable
          query={debouncedQuery}
          sort={sort}
          onSort={setSort}
          isDirectMessage
          leaderGroupId={selectedLeaderGroup}
        />
      </TabPane>
    </ModuleWrapper>
  )
}
